/**
 * API utility for claim-based verification analysis
 * Uses LLM to decompose statements into atomic factual claims and verify them against evidence
 */

import {callLLM} from './llmApi';

/**
 * Extract atomic claims from a statement (without verification)
 * @param {string} statement - The statement to decompose
 * @returns {Promise<Object>} - { claims: Array<Claim>, extraction_metadata }
 */
export async function extractClaims(statement) {
    if (!statement || !statement.trim()) {
        console.warn('Empty statement provided to claim extraction');
        return {
            claims: [],
            extraction_metadata: {
                statement_hash: '',
                extracted_at: new Date().toISOString()
            }
        };
    }

    const systemMessage = getExtractionSystemMessage();
    const userMessage = JSON.stringify({statement});

    const messages = [
        {role: 'system', content: systemMessage},
        {role: 'user', content: userMessage}
    ];

    const model = 'gpt-5.2';
    const options = {
        temperature: 0,
        top_p: 1.0,
        frequency_penalty: 0.0,
        presence_penalty: 0.0
    };

    try {
        console.log('Calling LLM for claim extraction with model:', model);
        const response = await callLLM(messages, model, options);

        const content = response.choices?.[0]?.message?.content || response.content;

        if (!content) {
            throw new Error('No content in LLM response');
        }

        let parsedResult;
        try {
            const jsonMatch = content.match(/```(?:json)?\s*({[\s\S]*})\s*```/);
            if (jsonMatch) {
                parsedResult = JSON.parse(jsonMatch[1]);
            } else {
                parsedResult = JSON.parse(content);
            }
        } catch (parseError) {
            console.error('Failed to parse LLM response as JSON:', parseError);
            console.error('Raw content:', content);
            throw new Error('LLM response was not valid JSON');
        }

        // Normalize claims
        const claims = (parsedResult.claims || []).map((claim, index) => ({
            id: claim.id ?? index + 1,
            claim: claim.claim ?? '',
            importance: claim.importance ?? 'medium',
            semantic_query: claim.semantic_query ?? claim.claim ?? '',
            keywords_query: claim.keywords_query || claim.boolean_query || ''
        }));

        return {
            claims,
            extraction_metadata: parsedResult.extraction_metadata || {
                statement_hash: '',
                extracted_at: new Date().toISOString()
            }
        };
    } catch (error) {
        console.error('Error extracting claims:', error);
        return {
            claims: [],
            extraction_metadata: {
                statement_hash: '',
                extracted_at: new Date().toISOString()
            }
        };
    }
}

/**
 * Verify pre-extracted claims against evidence
 * @param {Array<Object>} claims - Previously extracted claims
 * @param {Array<Object>} evidence - Array of {referenceId, text}
 * @returns {Promise<Object>} - { summary, verified_claims, evidence_scores }
 */
export async function verifyClaims(claims, evidence = []) {
    if (!Array.isArray(claims) || claims.length === 0) {
        console.warn('No claims provided to verification');
        return createEmptyVerification();
    }

    // Validate evidence format
    if (!Array.isArray(evidence)) {
        console.error('Evidence must be an array');
        return createEmptyVerification();
    }

    const hasNewFormat = evidence.length > 0 && evidence.every(e =>
        e && typeof e === 'object' && e.referenceId && e.text
    );

    if (evidence.length > 0 && !hasNewFormat) {
        console.error('Evidence must be array of {referenceId, text} objects');
        return createEmptyVerification();
    }

    const systemMessage = getVerificationSystemMessage();
    const userMessage = JSON.stringify({claims, evidence});

    const messages = [
        {role: 'system', content: systemMessage},
        {role: 'user', content: userMessage}
    ];

    const model = 'gpt-5.2';
    const options = {
        temperature: 0,
        top_p: 1.0,
        frequency_penalty: 0.0,
        presence_penalty: 0.0
    };

    try {
        console.log('Calling LLM for claim verification with model:', model);
        const response = await callLLM(messages, model, options);

        const content = response.choices?.[0]?.message?.content || response.content;

        if (!content) {
            throw new Error('No content in LLM response');
        }

        let parsedResult;
        try {
            const jsonMatch = content.match(/```(?:json)?\s*({[\s\S]*})\s*```/);
            if (jsonMatch) {
                parsedResult = JSON.parse(jsonMatch[1]);
            } else {
                parsedResult = JSON.parse(content);
            }
        } catch (parseError) {
            console.error('Failed to parse LLM response as JSON:', parseError);
            console.error('Raw content:', content);
            throw new Error('LLM response was not valid JSON');
        }

        return normalizeVerificationResult(parsedResult, evidence);
    } catch (error) {
        console.error('Error verifying claims:', error);
        return createEmptyVerification();
    }
}

/**
 * Analyze claims with evidence using LLM-based factual verification
 * This is now a facade that can orchestrate extractClaims and verifyClaims
 * or use the legacy combined approach for backward compatibility
 * @param {string} statement - The statement to decompose and verify
 * @param {Array<Object>} evidence - Array of evidence objects with {referenceId, text}
 * @param {Object|null} previousAnalysis - Previous analysis result (for incremental updates)
 * @returns {Promise<Object>} Analysis result with claims, summary, and evidence scores
 */
export async function analyzeClaimsWithEvidence(statement, evidence = [], previousAnalysis = null) {
    if (!statement || !statement.trim()) {
        console.warn('Empty statement provided to claim analysis');
        return createEmptyAnalysis();
    }

    // Validate evidence format
    if (!Array.isArray(evidence)) {
        console.error('Evidence must be an array');
        return createEmptyAnalysis();
    }

    // Check if evidence has new format {referenceId, text}
    const hasNewFormat = evidence.length > 0 && evidence.every(e =>
        e && typeof e === 'object' && e.referenceId && e.text
    );

    if (evidence.length > 0 && !hasNewFormat) {
        console.error('Evidence must be array of {referenceId, text} objects');
        return createEmptyAnalysis();
    }

    // For now, use the legacy combined approach for backward compatibility
    // In the future, this can be optimized to use extractClaims + verifyClaims
    // when we detect that only evidence has changed
    const systemMessage = getSystemMessage();

    const userMessage = JSON.stringify({
        statement,
        evidence,
        previous_analysis: previousAnalysis
    });

    const messages = [
        {role: 'system', content: systemMessage},
        {role: 'user', content: userMessage}
    ];

    const model = 'gpt-5.2';

    // Options for deterministic and controlled responses
    const options = {
        temperature: 0, // Deterministic responses - same input produces same output
        top_p: 1.0, // Use full probability distribution (with temp=0, this ensures determinism)
        frequency_penalty: 0.0, // No penalty for repetition (we want consistent structure)
        presence_penalty: 0.0 // No penalty for using same concepts (consistency is key)
    };

    try {
        console.log('Calling LLM for claim analysis with model:', model);
        const response = await callLLM(messages, model, options);

        // Extract content from response
        const content = response.choices?.[0]?.message?.content || response.content;

        if (!content) {
            throw new Error('No content in LLM response');
        }

        // Parse the JSON response
        let parsedResult;
        try {
            // Try to extract JSON from markdown code blocks if present
            const jsonMatch = content.match(/```(?:json)?\s*({[\s\S]*})\s*```/);
            if (jsonMatch) {
                parsedResult = JSON.parse(jsonMatch[1]);
            } else {
                parsedResult = JSON.parse(content);
            }
        } catch (parseError) {
            console.error('Failed to parse LLM response as JSON:', parseError);
            console.error('Raw content:', content);
            throw new Error('LLM response was not valid JSON');
        }

        // Validate and normalize the response structure
        return normalizeAnalysisResult(parsedResult, evidence);
    } catch (error) {
        console.error('Error analyzing claims with evidence:', error);
        return createEmptyAnalysis();
    }
}

/**
 * Create an empty analysis result structure
 * @returns {Object} Empty analysis result
 */
function createEmptyAnalysis() {
    return {
        summary: {
            total_claims: 0,
            proven_claims: 0,
            partially_proven_claims: 0,
            contradicted_claims: 0,
            unproven_claims: 0,
            importance_distribution: {
                high: 0,
                medium: 0,
                low: 0
            }
        },
        claims: [],
        evidence_scores: {},
        analysis_metadata: {
            evidence_added: [],
            evidence_removed: [],
            claims_reused: false,
            full_recalculation: true
        }
    };
}

/**
 * Create an empty verification result structure
 * @returns {Object} Empty verification result
 */
function createEmptyVerification() {
    return {
        summary: {
            total_claims: 0,
            proven_claims: 0,
            partially_proven_claims: 0,
            contradicted_claims: 0,
            unproven_claims: 0,
            importance_distribution: {
                high: 0,
                medium: 0,
                low: 0
            }
        },
        verified_claims: [],
        evidence_scores: {}
    };
}

/**
 * Normalize and validate the LLM response structure
 * @param {Object} result - Raw LLM response
 * @param {Array<Object>} evidence - Array of evidence objects with {referenceId, text}
 * @returns {Object} Normalized analysis result
 */
function normalizeAnalysisResult(result, evidence) {
    // Ensure summary exists with all required fields
    const summary = {
        total_claims: result.summary?.total_claims ?? result.claims?.length ?? 0,
        proven_claims: result.summary?.proven_claims ?? 0,
        partially_proven_claims: result.summary?.partially_proven_claims ?? 0,
        contradicted_claims: result.summary?.contradicted_claims ?? 0,
        unproven_claims: result.summary?.unproven_claims ?? 0,
        importance_distribution: {
            high: result.summary?.importance_distribution?.high ?? 0,
            medium: result.summary?.importance_distribution?.medium ?? 0,
            low: result.summary?.importance_distribution?.low ?? 0
        }
    };

    // Ensure claims array exists and has required fields
    const claims = (result.claims || []).map((claim, index) => ({
        id: claim.id ?? index + 1,
        claim: claim.claim ?? '',
        importance: claim.importance ?? 'medium',
        status: claim.status ?? 'not_proven',
        supporting_evidence: Array.isArray(claim.supporting_evidence) ? claim.supporting_evidence : [],
        contradicting_evidence: Array.isArray(claim.contradicting_evidence) ? claim.contradicting_evidence : [],
        semantic_query: claim.semantic_query ?? claim.claim ?? '',
        keywords_query: claim.keywords_query || claim.boolean_query || ''
    }));

    // Convert evidence_scores to object keyed by referenceId
    const evidence_scores = {};

    // If result has evidence_scores as object (new format), use it directly
    if (result.evidence_scores && typeof result.evidence_scores === 'object' && !Array.isArray(result.evidence_scores)) {
        // LLM returned object format - validate and normalize
        for (const refId of evidence.map(e => e.referenceId)) {
            if (result.evidence_scores[refId]) {
                evidence_scores[refId] = {
                    score: typeof result.evidence_scores[refId].score === 'number' ? result.evidence_scores[refId].score : 0.0,
                    reason: result.evidence_scores[refId].reason || 'No analysis available'
                };
            } else {
                // Missing score for this referenceId
                evidence_scores[refId] = {
                    score: 0.0,
                    reason: 'No analysis available for this evidence'
                };
            }
        }
    } else if (Array.isArray(result.evidence_scores)) {
        // LLM returned array format (old format) - convert to object using evidence array indices
        evidence.forEach((ev, index) => {
            const score = result.evidence_scores[index];
            evidence_scores[ev.referenceId] = {
                score: typeof score?.score === 'number' ? score.score : 0.0,
                reason: score?.reason || 'No analysis available for this evidence'
            };
        });
    } else {
        // No evidence_scores in result - create defaults
        evidence.forEach(ev => {
            evidence_scores[ev.referenceId] = {
                score: 0.0,
                reason: 'No analysis available for this evidence'
            };
        });
    }

    // Extract or create analysis_metadata
    const analysis_metadata = {
        evidence_added: result.analysis_metadata?.evidence_added || [],
        evidence_removed: result.analysis_metadata?.evidence_removed || [],
        claims_reused: result.analysis_metadata?.claims_reused ?? false,
        full_recalculation: result.analysis_metadata?.full_recalculation ?? true
    };
    claims.forEach(claim => {
        console.log('[SEMANTIC QUERY]', claim.semantic_query);
    });

    return {
        summary,
        claims,
        evidence_scores,
        analysis_metadata
    };
}

/**
 * Normalize and validate verification result
 * @param {Object} result - Raw LLM verification response
 * @param {Array<Object>} evidence - Array of evidence objects with {referenceId, text}
 * @returns {Object} Normalized verification result
 */
function normalizeVerificationResult(result, evidence) {
    // Ensure summary exists with all required fields
    const summary = {
        total_claims: result.summary?.total_claims ?? 0,
        proven_claims: result.summary?.proven_claims ?? 0,
        partially_proven_claims: result.summary?.partially_proven_claims ?? 0,
        contradicted_claims: result.summary?.contradicted_claims ?? 0,
        unproven_claims: result.summary?.unproven_claims ?? 0,
        importance_distribution: {
            high: result.summary?.importance_distribution?.high ?? 0,
            medium: result.summary?.importance_distribution?.medium ?? 0,
            low: result.summary?.importance_distribution?.low ?? 0
        }
    };

    // Normalize verified claims
    const verified_claims = (result.verified_claims || []).map(vc => ({
        id: vc.id,
        status: vc.status ?? 'not_proven',
        supporting_evidence: Array.isArray(vc.supporting_evidence) ? vc.supporting_evidence : [],
        contradicting_evidence: Array.isArray(vc.contradicting_evidence) ? vc.contradicting_evidence : []
    }));

    // Normalize evidence scores
    const evidence_scores = {};
    if (result.evidence_scores && typeof result.evidence_scores === 'object' && !Array.isArray(result.evidence_scores)) {
        for (const refId of evidence.map(e => e.referenceId)) {
            if (result.evidence_scores[refId]) {
                evidence_scores[refId] = {
                    score: typeof result.evidence_scores[refId].score === 'number' ? result.evidence_scores[refId].score : 0.0,
                    reason: result.evidence_scores[refId].reason || 'No analysis available'
                };
            } else {
                evidence_scores[refId] = {
                    score: 0.0,
                    reason: 'No analysis available for this evidence'
                };
            }
        }
    } else {
        // Create defaults
        evidence.forEach(ev => {
            evidence_scores[ev.referenceId] = {
                score: 0.0,
                reason: 'No analysis available for this evidence'
            };
        });
    }

    return {
        summary,
        verified_claims,
        evidence_scores
    };
}

/**
 * Get a human-readable score label based on numeric score
 * @param {number} score - Numeric score from 0.0 to 1.0
 * @returns {string} Human-readable label
 */
export function getScoreLabel(score) {
    if (score >= 0.9) return 'Excellent';
    if (score >= 0.7) return 'Good';
    if (score >= 0.5) return 'Moderate';
    if (score >= 0.3) return 'Weak';
    return 'Poor';
}

/**
 * Get MUI color name for a score
 * @param {number} score - Numeric score from 0.0 to 1.0
 * @returns {string} MUI color name
 */
export function getScoreColor(score) {
    if (score >= 0.9) return 'success.dark';
    if (score >= 0.7) return 'success.main';
    if (score >= 0.5) return 'warning.main';
    if (score >= 0.3) return 'warning.dark';
    return 'error.main';
}

/**
 * Merge extracted claims with verification results
 * @param {Array} extractedClaims - Claims from extraction
 * @param {Object} verification - Verification result
 * @returns {Object} Combined analysis result
 */
export function mergeClaimsWithVerification(extractedClaims, verification) {
    // Merge claim data with verification status
    const claims = extractedClaims.map(claim => {
        const verifiedClaim = verification.verified_claims?.find(vc => vc.id === claim.id);
        return {
            ...claim,
            status: verifiedClaim?.status || 'not_proven',
            supporting_evidence: verifiedClaim?.supporting_evidence || [],
            contradicting_evidence: verifiedClaim?.contradicting_evidence || []
        };
    });

    return {
        summary: verification.summary,
        claims,
        evidence_scores: verification.evidence_scores,
        analysis_metadata: {
            evidence_added: [],
            evidence_removed: [],
            claims_reused: false,
            full_recalculation: true
        }
    };
}

function getExtractionSystemMessage() {
    return `You are a factual-claim extraction engine.

TASK:
1. Decompose the input statement into all atomic factual claims.
2. Assign importance ranking (high/medium/low) to each claim.
3. Generate semantic search queries for each claim.
4. Generate keywords queries with logical operators for each claim.
5. Return ONLY valid JSON - no explanations.

INPUT FORMAT:
{
  "statement": "text to decompose"
}

OUTPUT FORMAT (STRICT JSON):
{
  "claims": [
    {
      "id": 1,
      "claim": "extracted atomic claim text",
      "importance": "high" | "medium" | "low",
      "semantic_query": "optimized search query for semantic/embedding search",
      "keywords_query": "search expression with logical operators (& for AND, | for OR)"
    }
  ],
  "extraction_metadata": {
    "statement_hash": "hash of original statement",
    "extracted_at": "ISO timestamp"
  }
}

EXTRACTION RULES:
1️⃣ Claim Decomposition
• Extract ALL atomic factual claims (split compounds).
• One verifiable fact per claim.
• Preserve original meaning.
• Do NOT add external knowledge or infer unstated implications.

2️⃣ Importance Ranking
• High: Core actor-action-object, primary events
• Medium: Quantities, Dates, Locations
• Low: Descriptive modifiers, secondary context

3️⃣ Semantic Query Rules
• Use natural language, optimized for embeddings
• Include entities & actions, avoid filler
• Be self-contained
Example: "North Korea deployed soldiers to Russia military cooperation"

4️⃣ Keywords Query Rules
• Use logical operators: & (AND), | (OR)
• Group alternatives with parentheses
• Use quotes for exact phrases
• Include key terms and entities
• Make searchable in keyword-based search systems
Example: "(Russia | Russian | Moscow | DPRK | \\"North Korea\\") & (military | cooperation | partnership | transfer | weapons)"

5️⃣ Determinism
• Stable field ordering
• Claim IDs start at 1 and increment
• No extra fields
• Strict JSON (no trailing commas)`;
}

function getVerificationSystemMessage() {
    return `You are a factual-claim verification engine.

TASK:
Given pre-extracted claims and evidence, determine:
1. Which claims are proven, partially_proven, not_proven, or contradicted
2. Which evidence supports or contradicts each claim
3. Score each evidence piece for overall relevance

INPUT FORMAT:
{
  "claims": [
    {
      "id": 1,
      "claim": "atomic claim text",
      "importance": "high|medium|low",
      "semantic_query": "...",
      "keywords_query": "..."
    }
  ],
  "evidence": [
    {
      "referenceId": "unique-id-1",
      "text": "evidence text content"
    }
  ]
}

OUTPUT FORMAT (STRICT JSON):
{
  "summary": {
    "total_claims": number,
    "proven_claims": number,
    "partially_proven_claims": number,
    "contradicted_claims": number,
    "unproven_claims": number,
    "importance_distribution": {
      "high": number,
      "medium": number,
      "low": number
    }
  },
  "verified_claims": [
    {
      "id": 1,
      "status": "proven" | "partially_proven" | "not_proven" | "contradicted",
      "supporting_evidence": ["referenceId1", "referenceId2"],
      "contradicting_evidence": []
    }
  ],
  "evidence_scores": {
    "referenceId1": {
      "score": 0.8,
      "reason": "string explanation of why this evidence's value was assigned (for user guidance messages)"
    }
  }
}

VERIFICATION RULES:
1️⃣ Do NOT Modify Claims
• Only verify the provided claims - do NOT add, remove, or change them
• Match claim IDs exactly from input

2️⃣ Status Definitions
• proven: All aspects of the claim are fully supported by evidence
• partially_proven: Some aspects of the claim are supported, but not all
• not_proven: No evidence supports the claim (but no contradiction)
• contradicted: Evidence directly contradicts the claim

3️⃣ Evidence Rules
• Use ONLY provided evidence texts
• Support/Contradiction must be explicit
• A single evidence may support/contradict multiple claims if relevant
• If neither supports nor contradicts: status = not_proven

4️⃣ Evidence Scoring
• Score each evidence (0.0 to 1.0) based on:
  - How well it proves/disproves claims
  - Explicitness and factual accuracy
  - Relevance and specificity
• Provide reason string explaining the score (for user-facing guidance)
• The evidence score MUST depend on whether the evidence proves claims

5️⃣ Summary Calculation
• total_claims = # of claims
• proven_claims = count(status == proven)
• partially_proven_claims = count(status == partially_proven)
• contradicted_claims = count(status == contradicted)
• unproven_claims = count(status == not_proven)
• importance_distribution: counts for each level from input claims

All totals must be mathematically consistent.

6️⃣ Determinism
• Stable field ordering
• No extra fields
• Strict JSON (no trailing commas)`;
}

function getSystemMessage() {
    return `Developer: Developer: You are a factual-claim verification engine.

TASK OVERVIEW
1. Decompose the input statement into all atomic factual claims.
2. Assign supporting or contradicting evidence using ONLY the provided evidence array. Note: A single evidence excerpt may support or contradict multiple claims if relevant.
3. Detect explicit contradictions.
4. Rank claims by importance.
5. Generate the following outputs:
    • Embedding-optimized semantic search query
    • Keywords query with logical operators (& for AND, | for OR)
    • Strictly valid JSON (no explanations)
    • Variable number of claims
    • Evidence value scores assigned for each evidence excerpt, with a 'reason' prop for each score to show user-facing message-guidance. The evidence score must depend on whether the evidence proves the claim or not.

INPUT FORMAT
{
  "statement": "...",
  "evidence": [
    {
      "referenceId": "unique-id-1",
      "text": "evidence text content"
    },
    {
      "referenceId": "unique-id-2",
      "text": "evidence text content"
    }
  ],
  "previous_analysis": {
    "claims": [...],
    "evidence_scores": {...}
  } // Optional: provided for incremental updates
}

OUTPUT FORMAT (STRICT JSON)
{
  "summary": {
    "total_claims": number,
    "proven_claims": number,
    "partially_proven_claims": number,
    "contradicted_claims": number,
    "unproven_claims": number,
    "importance_distribution": {
      "high": number,
      "medium": number,
      "low": number
    }
  },
  "claims": [
    {
      "id": integer, // Starts at 1, sequential
      "claim": "atomic factual statement",
      "importance": "high" | "medium" | "low",
      "status": "proven" | "partially_proven" | "not_proven" | "contradicted",
      "supporting_evidence": [
        {
          "referenceId": "unique-id-1",
          "text": "exact excerpt"
        }
      ],
      "contradicting_evidence": [
        {
          "referenceId": "unique-id-2",
          "text": "exact excerpt"
        }
      ],
      "semantic_query": "embedding-optimized search query",
      "keywords_query": "search expression with logical operators (& for AND, | for OR)"
    }
  ],
  "evidence_scores": {
    "unique-id-1": {
      "score": number,
      "reason": "string explanation of why this evidence's value was assigned (for user guidance messages)"
    },
    "unique-id-2": {
      "score": number,
      "reason": "string explanation"
    }
  }, // Object keyed by referenceId
  "analysis_metadata": {
    "evidence_added": ["unique-id-3"],
    "evidence_removed": ["unique-id-0"],
    "claims_reused": boolean,
    "full_recalculation": boolean
  }
}

FIELD CONSTRAINTS
• Never omit required fields.
• If no supporting or contradicting evidence: use empty arrays.
• evidence_scores must be an object with keys matching the referenceId from input evidence array.
• Each evidence score must have both 'score' (number) and 'reason' (string) fields.
• supporting_evidence and contradicting_evidence must use referenceId (not array indices).

INCREMENTAL ANALYSIS (when previous_analysis provided)

When the input includes "previous_analysis":

1. **Identify Evidence Changes:**
   - NEW evidence: referenceIds in current evidence but NOT in previous evidence_scores
   - REMOVED evidence: referenceIds in previous evidence_scores but NOT in current evidence
   - UNCHANGED evidence: referenceIds present in both

2. **Claim Reuse Logic:**
   - IF statement is EXACTLY the same as before:
     - REUSE previous claims (maintain same IDs and decomposition)
     - Update claim status based on ALL current evidence (new + unchanged)
     - Update supporting_evidence and contradicting_evidence arrays
   - IF statement has changed:
     - Perform FULL decomposition (new claim IDs)

3. **Evidence Score Calculation:**
   - For UNCHANGED evidence with status still valid: COPY previous scores
   - For NEW evidence: Calculate new scores
   - For evidence where claim status changed: Recalculate score
   - ALWAYS output scores for ALL current evidence
   - The evidence score must depend on whether the evidence proves the claim or not.

4. **Output Metadata:**
   - Include "analysis_metadata" field with:
     - evidence_added: array of new referenceIds
     - evidence_removed: array of removed referenceIds
     - claims_reused: boolean
     - full_recalculation: boolean

5. **Optimization:**
   - Don't re-read unchanged evidence text in detail
   - Focus compute on new evidence evaluation
   - Maintain consistency in claim-evidence links

EXTRACTION AND VALIDATION RULES
1️⃣ Claim Decomposition
• Extract ALL atomic factual claims (split compounds).
• One verifiable fact per claim.
• Preserve original meaning.
• Do NOT add external knowledge or infer unstated implications.

2️⃣ Evidence Rules
• Use ONLY provided "evidence" texts.
• Support/Contradiction: must be explicit.
• Quote exact excerpts. Partial implication = not_proven.
• If neither: status = not_proven.
• A single evidence excerpt may support or contradict multiple claims if relevant.
• Assign a value score (in 'evidence_scores') for each evidence excerpt based on how factually and explicitly the evidence supports or contradicts the statement, not just on vague mentions (consider explicitness, factual accuracy, relevance, and specificity). For each score, provide a 'reason' string explaining the score (for user-facing guidance). The evidence score must depend on whether the evidence proves the claim or not.

3️⃣ Status Logic
• proven → All aspects of the claim are fully supported by evidence
• partially_proven → Some aspects of the claim are supported, but not all
• not_proven → No evidence supports the claim (but no contradiction)
• contradicted → Evidence directly contradicts the claim

4️⃣ Importance Ranking
• High: Core actor-action-object, primary events
• Medium: Quantities, Dates, Locations
• Low: Descriptive modifiers, secondary context

5️⃣ Semantic Query Rules
• Use natural language, optimized for embeddings
• Include entities & actions, avoid filler
• Be self-contained
Example: "North Korea deployed soldiers to Russia military cooperation"

6️⃣ Keywords Query Rules
• Use logical operators: & (AND), | (OR)
• Group alternatives with parentheses
• Use quotes for exact phrases
• Include key terms and entities
• Make searchable in keyword-based search systems
Example: "(Russia | Russian | Moscow | DPRK | \\"North Korea\\") & (military | cooperation | partnership)"

7️⃣ Determinism Constraints
• Stable field ordering
• Claim IDs start at 1 and increment
• No extra fields
• Strict JSON (no trailing commas)

8️⃣ Summary Calculation
• total_claims = # of claims
• proven_claims = count(status == proven)
• partially_proven_claims = count(status == partially_proven)
• contradicted_claims = count(status == contradicted)
• unproven_claims = count(status == not_proven)
• importance_distribution: counts for each level (high/medium/low)

All totals must be mathematically consistent.`
}
